import type { FC } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import type { StringifiableRecord } from "query-string";
import queryString from "query-string";

import { translate } from "app/filters/i18nFilter";
import { WebsiteKeywordsPresets } from "app/pages/keyword-research/website-analysis/pages/seo-overview/competitive-opportunities/WebsiteKeywordsPresets";
import type { ICompetitiveOpportunitiesData } from "app/pages/keyword-research/website-analysis/pages/seo-overview/types";
import type { SourceType } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { getUrlComponents } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { default as DurationService } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import type { EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";

import { MetricsRow, MetricTitle } from "../StyledComponent";

import { KeywordsGap } from "./keywords-gap/KeywordsGap";
import { KeywordsGapFilters } from "./KeywordsGapFilters";
import { KeywordsGapContentWrapper, KeywordsGapWrapper } from "./StyledComponents";

const ENDPOINT_PRESETS_TOTAL = `widgetApi/WebsiteAnalysisV2/WebsiteAnalysis/PresetsTotal`;
export const CompetitiveOpportunities: FC = () => {
    const { params } = useCurrentStateAndParams();
    const {
        key,
        duration,
        country,
        webSource,
        pageFilter: pageFilterJson,
        vennDiagramSourceType,
        competitorsFilter: competitorsFilterJson,
    } = params;
    const pageFilter: IChosenItemParams = safeJsonParse(pageFilterJson as string);
    const competitorsFilter: IChosenItemParams = safeJsonParse(competitorsFilterJson as string);
    const competitorsDomains = competitorsFilter.map((k) => getUrlComponents(k.url).domain);
    const finalKeys: string[] = [...new Set([key as string, ...competitorsDomains])];
    const finalPageFilter: IChosenItemParams = [...pageFilter, ...competitorsFilter];
    const finalCountry: number = country as number;
    const finalSourceType = SourceTypeMap[vennDiagramSourceType as SourceType];
    const { from, to, isWindow } = DurationService.getDurationData(duration as string).forAPI;
    const apiParams: StringifiableRecord = {
        to,
        from,
        isWindow,
        country: finalCountry,
        webSource: webSource as EDeviceType,
        keys: finalKeys.join(","),
        includeSubDomains: true,
        pageFilterJson: transformSiteListToParam(finalPageFilter),
        sourceType: finalSourceType,
    };
    const queryParams = queryString.stringify(apiParams);
    const { data: websiteKeywordsPresetsData, isLoading: websiteKeywordsPresetsLoading } = useQuery({
        queryKey: [
            ENDPOINT_PRESETS_TOTAL,
            to,
            from,
            isWindow,
            finalCountry,
            webSource,
            finalKeys,
            finalPageFilter,
            vennDiagramSourceType,
        ],
        queryFn: async () =>
            DefaultFetchService.getInstance().post<ICompetitiveOpportunitiesData>(
                `${ENDPOINT_PRESETS_TOTAL}?${queryParams}`,
                [],
            ),
    });
    return (
        <MetricsRow
            gap={"16px"}
            marginBottom="32px"
        >
            <MetricTitle>
                {translate("wa.traffic.sources.search.seo.overview.competitive.opportunities.title")}
            </MetricTitle>
            <KeywordsGapWrapper gap="20px">
                <KeywordsGapFilters />
                <KeywordsGapContentWrapper gap={"24px"}>
                    <WebsiteKeywordsPresets
                        isLoading={websiteKeywordsPresetsLoading}
                        data={websiteKeywordsPresetsData}
                        pageParams={{
                            pageFilter: finalPageFilter,
                            keys: finalKeys,
                            sourceType: finalSourceType,
                            country: finalCountry,
                        }}
                    />
                    <KeywordsGap />
                </KeywordsGapContentWrapper>
            </KeywordsGapWrapper>
        </MetricsRow>
    );
};
